import React, { useState, type FC, type Ref } from 'react';
import Button from '@atlaskit/button/new';
import { Box, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { premiumLandingPageEntryPoint } from '@atlassian/jira-servicedesk-premium-landing-page/entrypoint.tsx';
import { Trigger, usePanel } from '@atlassian/entry-point-panel';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { useUrlManager } from '../hooks/use-url-manager/index.tsx';
import messages from './messages.tsx';
import type { PremiumLandingPageEntryPointButtonProps } from './types.tsx';

const panelBoxStyles = xcss({
	position: 'absolute',
	top: '0',
	left: '0',
	width: '1px',
	height: '1px',
});

export const PremiumLandingPageEntryPointButtonWithDrawer: FC<
	PremiumLandingPageEntryPointButtonProps
> = ({ origin, buttonLabel }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { formatMessage } = useIntl();
	useUrlManager('jsmPremiumBenefitsOpen', 'true', isModalOpen);

	const onEntryPointButtonClick = (analyticsEvent: UIAnalyticsEvent) => {
		setIsModalOpen(true);
		fireUIAnalytics(analyticsEvent, 'premiumLandingPageEntryPointButton', {
			entryPointOrigin: origin,
		});
	};

	const { Panel, trigger } = usePanel();

	return (
		<>
			{fg('jsm_premium_landing_page_post_mvp') && (
				<Box xcss={panelBoxStyles}>
					<Panel />
				</Box>
			)}
			<Trigger
				trigger={trigger}
				entryPoint={premiumLandingPageEntryPoint}
				entryPointProps={{
					origin,
					onClose: () => {
						setIsModalOpen(false);
						trigger.setPanel(null);
					},
				}}
			>
				{({ ref }) => (
					<Button
						appearance="subtle"
						onClick={(
							_: React.MouseEvent<HTMLElement, MouseEvent>,
							analyticsEvent: UIAnalyticsEvent,
						) => onEntryPointButtonClick(analyticsEvent)}
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						ref={ref as Ref<HTMLButtonElement>}
					>
						{formatMessage(buttonLabel ?? messages.defaultMessage)}
					</Button>
				)}
			</Trigger>
			{!fg('jsm_premium_landing_page_post_mvp') && <Panel />}
		</>
	);
};
